import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type FormEvent,
  type PointerEvent as ReactPointerEvent,
} from "react";
import { createPortal } from "react-dom";
import { useStoreSelector } from "../../../../src/lib/store";
import { formatNoteName } from "../../../../src/lib/audio";
import { getLabActions } from "../labActions";
import { getTrackerGrid } from "../tracker/trackerGridRegistry";
import {
  ARP_MODES,
  buildGeneratedRows,
  type ArpMode,
} from "../lib/generateNotes";
import { patchUi, uiStore } from "../store/uiStore";

const STORAGE_KEY = "audio-lab:generate-notes-modal";
const PANEL_WIDTH = 360;
const PANEL_MIN_HEIGHT = 280;

type PanelPos = { left: number; top: number };

function loadPos(): PanelPos | undefined {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) {
      return undefined;
    }
    const parsed = JSON.parse(raw) as Partial<PanelPos>;
    if (typeof parsed.left === "number" && typeof parsed.top === "number") {
      return { left: parsed.left, top: parsed.top };
    }
  } catch {
    // ignore
  }
  return undefined;
}

function savePos(pos: PanelPos): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
  } catch {
    // ignore
  }
}

function defaultPos(): PanelPos {
  const left = Math.max(12, (window.innerWidth - PANEL_WIDTH) / 2);
  const top = Math.max(12, Math.min(96, window.innerHeight * 0.12));
  return { left, top };
}

function clampPos(pos: PanelPos, width: number, height: number): PanelPos {
  const maxLeft = Math.max(8, window.innerWidth - width - 8);
  const maxTop = Math.max(
    8,
    window.innerHeight - Math.min(height, window.innerHeight - 16) - 8,
  );
  return {
    left: Math.min(maxLeft, Math.max(8, pos.left)),
    top: Math.min(maxTop, Math.max(8, pos.top)),
  };
}

/**
 * Floating Generate Notes tool — same chrome/behavior as Help / Author:
 * no backdrop, draggable title bar, persisted position, stays open after Generate.
 */
export function GenerateNotesModal() {
  const open = useStoreSelector(uiStore, s => s.generateNotesOpen);
  const panelRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const dragCleanupRef = useRef<(() => void) | undefined>(undefined);
  const [pos, setPos] = useState<PanelPos>(() => loadPos() ?? { left: 12, top: 12 });
  const [dragging, setDragging] = useState(false);
  const [pattern, setPattern] = useState("CM7:4 Dm7 G7");
  const [gap, setGap] = useState(0);
  const [mode, setMode] = useState<ArpMode>("as-played");
  const [error, setError] = useState<string | null>(null);
  const [targetTick, setTargetTick] = useState(0);

  const target = useMemo(() => {
    if (!open) {
      return null;
    }
    void targetTick;
    return getTrackerGrid()?.getGenerateNotesTarget() ?? null;
  }, [open, targetTick]);

  const preview = useMemo(() => {
    if (!open || !target) {
      return null;
    }
    return buildGeneratedRows({
      pattern,
      mode,
      gap,
      rowCount: target.rowCount,
    });
  }, [open, pattern, mode, gap, target]);

  useEffect(() => {
    if (!open) {
      return;
    }
    setError(null);
    setTargetTick(t => t + 1);
    setPos(prev => {
      const el = panelRef.current;
      const width = el?.offsetWidth || PANEL_WIDTH;
      const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(loadPos() ?? defaultPos(), width, height);
      savePos(next);
      return next.left === prev.left && next.top === prev.top ? prev : next;
    });
    const id = requestAnimationFrame(() => {
      inputRef.current?.focus();
      inputRef.current?.select();
    });
    return () => cancelAnimationFrame(id);
  }, [open]);

  useEffect(() => {
    if (!open) {
      return;
    }
    function onResize(): void {
      setPos(prev => {
        const el = panelRef.current;
        const width = el?.offsetWidth || PANEL_WIDTH;
        const height = el?.offsetHeight || PANEL_MIN_HEIGHT;
        const next = clampPos(prev, width, height);
        savePos(next);
        return next;
      });
    }
    function onKey(event: KeyboardEvent): void {
      if (event.key !== "Escape") {
        return;
      }
      event.preventDefault();
      event.stopPropagation();
      close();
    }
    window.addEventListener("resize", onResize);
    window.addEventListener("keydown", onKey, true);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("keydown", onKey, true);
      dragCleanupRef.current?.();
      dragCleanupRef.current = undefined;
    };
  }, [open]);

  function close(): void {
    dragCleanupRef.current?.();
    dragCleanupRef.current = undefined;
    setDragging(false);
    patchUi({ generateNotesOpen: false });
    getLabActions()?.focusTracker?.();
  }

  function beginDrag(event: ReactPointerEvent): void {
    if (event.button !== 0) {
      return;
    }
    const targetEl = event.target as HTMLElement | null;
    if (targetEl?.closest("button, input, select, textarea, label")) {
      return;
    }
    event.preventDefault();
    const el = panelRef.current;
    if (!el) {
      return;
    }

    dragCleanupRef.current?.();

    const rect = el.getBoundingClientRect();
    const offsetX = event.clientX - rect.left;
    const offsetY = event.clientY - rect.top;
    setDragging(true);

    const onMove = (moveEvent: PointerEvent): void => {
      const width = el.offsetWidth || PANEL_WIDTH;
      const height = el.offsetHeight || PANEL_MIN_HEIGHT;
      const next = clampPos(
        {
          left: moveEvent.clientX - offsetX,
          top: moveEvent.clientY - offsetY,
        },
        width,
        height,
      );
      savePos(next);
      setPos(next);
    };

    const onUp = (): void => {
      setDragging(false);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      dragCleanupRef.current = undefined;
    };

    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    dragCleanupRef.current = onUp;
  }

  function onGenerate(event?: FormEvent): void {
    event?.preventDefault();
    setTargetTick(t => t + 1);
    const rowCount = getTrackerGrid()?.getGenerateNotesTarget().rowCount ?? 1;
    const built = buildGeneratedRows({
      pattern,
      mode,
      gap,
      rowCount,
    });
    if (!built.ok || !built.rows) {
      setError(built.ok ? "Nothing to write." : built.error);
      return;
    }
    setError(null);
    getLabActions()?.applyGenerateNotes?.(built.rows);
  }

  if (!open) {
    return null;
  }

  const previewText = preview?.ok && preview.rows
    ? preview.rows
        .map(n => (n == null ? "····" : formatNoteName(n)))
        .slice(0, 16)
        .join(" ")
      + (preview.rows.length > 16 ? "…" : "")
    : null;

  return createPortal(
    <div
      ref={panelRef}
      className={`help-modal generate-notes-modal fixed z-50 flex w-[min(360px,calc(100vw-24px))] flex-col overflow-hidden rounded-lg border border-lab-line bg-lab-panel text-[12px] text-lab-text shadow-[0_24px_48px_rgba(0,0,0,0.45)]${dragging ? " select-none" : ""}`}
      role="dialog"
      aria-modal="false"
      aria-label="Generate notes"
      style={{ left: pos.left, top: pos.top }}
    >
      <div
        className="help-modal-header flex shrink-0 cursor-move touch-none select-none items-center justify-between gap-2 border-b border-lab-line bg-lab-panel-bar px-[0.65rem] py-2"
        onPointerDown={beginDrag}
      >
        <span className="help-modal-title text-[12px] font-semibold text-lab-text">
          Generate notes
        </span>
        <button
          type="button"
          className="help-modal-close rounded border border-transparent bg-transparent px-2 py-[0.2rem] text-[12px] text-lab-muted hover:border-lab-line hover:bg-white/[0.04] hover:text-lab-text"
          title="Close"
          aria-label="Close"
          onPointerDown={event => event.stopPropagation()}
          onClick={() => {
            close();
            getLabActions()?.setStatus?.("Generate notes closed.");
          }}
        >
          Close
        </button>
      </div>

      <form
        className="help-modal-body flex flex-col gap-[0.65rem] px-[0.85rem] pt-3 pb-4 leading-[1.45]"
        onSubmit={onGenerate}
      >
        <p className="m-0 text-[11px] text-lab-muted">
          Space-separated notes or chords. Octave sticks after{" "}
          <code className="rounded bg-black/25 px-1 py-px font-mono text-[11px] text-lab-text">:n</code>
          {" "}— e.g.{" "}
          <code className="rounded bg-black/25 px-1 py-px font-mono text-[11px] text-lab-text">CM7:4 Dm7 G7</code>
        </p>

        <label className="flex flex-col gap-1">
          <span className="text-[10px] font-semibold tracking-[0.04em] text-lab-muted uppercase">
            Pattern
          </span>
          <input
            ref={inputRef}
            type="text"
            className="w-full rounded border border-lab-line bg-lab-bg px-2 py-[0.4rem] font-mono text-[13px] text-lab-text outline-none focus:border-lab-accent focus:shadow-[0_0_0_2px_rgba(61,139,253,0.25)]"
            spellCheck={false}
            value={pattern}
            onChange={event => {
              setPattern(event.target.value);
              setError(null);
            }}
            placeholder="CM7:4 Dm7 G7"
            aria-invalid={Boolean(error) || (preview != null && !preview.ok)}
          />
        </label>

        <div className="grid grid-cols-2 gap-2">
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold tracking-[0.04em] text-lab-muted uppercase">
              Gap
            </span>
            <input
              type="number"
              min={0}
              max={64}
              step={1}
              className="w-full rounded border border-lab-line bg-lab-bg px-2 py-[0.35rem] text-[13px] text-lab-text outline-none focus:border-lab-accent"
              value={gap}
              onChange={event => {
                const next = Number(event.target.value);
                setGap(Number.isFinite(next) ? Math.max(0, Math.min(64, Math.floor(next))) : 0);
              }}
            />
          </label>
          <label className="flex flex-col gap-1">
            <span className="text-[10px] font-semibold tracking-[0.04em] text-lab-muted uppercase">
              Behavior
            </span>
            <select
              className="w-full rounded border border-lab-line bg-lab-bg px-2 py-[0.35rem] text-[13px] text-lab-text outline-none focus:border-lab-accent"
              value={mode}
              onChange={event => setMode(event.target.value as ArpMode)}
            >
              {ARP_MODES.map(m => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
        </div>

        {target ? (
          <div className="text-[10px] text-lab-muted">
            Track I{target.track + 1} · rows {target.minStep}–{target.maxStep} ({target.rowCount})
            <button
              type="button"
              className="ml-2 border-0 bg-transparent p-0 text-[10px] text-lab-accent underline-offset-2 hover:underline"
              onClick={() => setTargetTick(t => t + 1)}
            >
              refresh
            </button>
          </div>
        ) : null}

        {previewText ? (
          <div className="truncate rounded border border-lab-line/70 bg-lab-bg/60 px-2 py-1.5 font-mono text-[11px] text-lab-text">
            {previewText}
          </div>
        ) : null}

        {(error || (preview && !preview.ok)) ? (
          <div className="text-[11px] text-rose-300">
            {error ?? (preview && !preview.ok ? preview.error : null)}
          </div>
        ) : null}

        <div className="flex justify-end gap-[0.35rem] pt-0.5">
          <button
            type="submit"
            className="rounded border border-lab-accent bg-[rgba(61,139,253,0.22)] px-[0.7rem] py-[0.35rem] text-[12px] text-white hover:bg-[rgba(61,139,253,0.32)]"
          >
            Generate
          </button>
        </div>
      </form>
    </div>,
    document.body,
  );
}
